import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ToastProvider } from './contexts/ToastContext';
import AppNav from './components/AppNav';
import './App.css';

// Lazy load route components for code splitting
const DealAnalyzer = lazy(() => import('./components/DealAnalyzer/DealAnalyzer'));
const Portfolio = lazy(() => import('./components/Portfolio/Portfolio'));
const AdminDashboard = lazy(() => import('./components/AdminDashboard/AdminDashboard'));
const ContactManagement = lazy(() => import('./components/ContactManagement/ContactManagement'));
const MarketAnalysisManager = lazy(
  () => import('./components/MarketAnalysis/MarketAnalysisManager')
);
const MyProperties = lazy(() => import('./components/MyProperties/MyProperties'));
const Sourcing = lazy(() => import('./components/Sourcing/Sourcing'));
const AuthScreen = lazy(() => import('./components/Auth/AuthScreen'));
const PlatformLandingPage = lazy(() => import('./components/Platform/PlatformLandingPage'));
const SubscriptionPage = lazy(() =>
  import('./components/Subscription/SubscriptionPage').then((m) => ({
    default: m.SubscriptionPage,
  }))
);
const SubscriptionSuccess = lazy(() =>
  import('./components/Subscription/SubscriptionSuccess').then((m) => ({
    default: m.SubscriptionSuccess,
  }))
);
const AcceptInvite = lazy(() => import('./components/AcceptInvite/AcceptInvite'));
const OnboardingSurvey = lazy(() =>
  import('./components/OnboardingSurvey').then((m) => ({ default: m.OnboardingSurvey }))
);
const SurveyReminderBanner = lazy(() =>
  import('./components/SurveyReminderBanner').then((m) => ({ default: m.SurveyReminderBanner }))
);
const ExpenseIncomePage = lazy(() =>
  import('./components/ExpenseIncome/ExpenseIncomePage').then((m) => ({
    default: m.ExpenseIncomePage,
  }))
);
const ReportsPage = lazy(() => import('./components/Reports/ReportsPage'));

// Loading fallback component
const PageLoader: React.FC = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: '24px', marginBottom: '10px' }}>Loading...</div>
    </div>
  </div>
);

const AuthenticatedApp: React.FC = () => {
  const { showOnboardingSurvey, dismissOnboardingSurvey } = useAuth();

  return (
    <div className="app-layout">
      <AppNav />
      <main className="main-content">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/app" element={<Navigate to="/app/deal-analyzer" replace />} />
            <Route path="/app/deal-analyzer" element={<DealAnalyzer />} />
            <Route
              path="/app/address-analyzer"
              element={<Navigate to="/app/deal-analyzer" replace />}
            />
            <Route path="/app/portfolio" element={<Portfolio />} />
            <Route path="/app/portfolios" element={<Navigate to="/app/portfolio" replace />} />
            <Route path="/app/market-analysis" element={<MarketAnalysisManager />} />
            <Route path="/app/my-properties" element={<MyProperties />} />
            <Route path="/app/sourcing" element={<Sourcing />} />
            <Route path="/app/expense-income" element={<ExpenseIncomePage />} />
            <Route path="/app/reports" element={<ReportsPage />} />
            <Route path="/app/subscription" element={<SubscriptionPage />} />
            <Route path="/app/subscription/success" element={<SubscriptionSuccess />} />
            <Route path="/app/admin" element={<AdminDashboard />} />
            <Route path="/app/cache-stats" element={<Navigate to="/app/admin" replace />} />
            <Route path="/app/contact-management" element={<ContactManagement />} />
            <Route path="*" element={<Navigate to="/app/deal-analyzer" replace />} />
          </Routes>
        </Suspense>
      </main>

      {/* Survey reminder banner - shows when user has pending survey */}
      <Suspense fallback={null}>
        <SurveyReminderBanner />
      </Suspense>

      {/* Survey modal - shows when triggered */}
      {showOnboardingSurvey && (
        <Suspense fallback={null}>
          <OnboardingSurvey onComplete={dismissOnboardingSurvey} onSkip={dismissOnboardingSurvey} />
        </Suspense>
      )}
    </div>
  );
};

const AppContent: React.FC = () => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const isAppRoute = location.pathname.startsWith('/app') || location.pathname === '/login';

  // Debug logging for routing issues
  React.useEffect(() => {
    console.log('Current URL:', window.location.href);
    console.log('Current pathname:', location.pathname);
  }, [location.pathname]);

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  // Show auth screen if user is trying to access app routes without being logged in
  if (!user && isAppRoute) {
    return (
      <Suspense fallback={<PageLoader />}>
        <AuthScreen />
      </Suspense>
    );
  }

  // If user is logged in and on app routes, show authenticated app
  if (user && isAppRoute) {
    return <AuthenticatedApp />;
  }

  if (user && location.pathname === '/pricing') {
    return <Navigate to="/app/subscription" replace />;
  }

  // Show platform landing for public routes
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<PlatformLandingPage />} />
        <Route path="/pricing" element={<Navigate to="/" replace />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/login" element={<AuthScreen />} />
        <Route path="/auth/*" element={<AuthScreen />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <ToastProvider>
          <Router>
            <div className="App">
              <AppContent />
            </div>
          </Router>
        </ToastProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
